import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("samara_token");
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("samara_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("samara_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    // Session-aware registration. Customer rule (18 Feb 2026): when an IBA
    // is already logged in and registers a new downline, their OWN session
    // must stay intact — the previous behaviour of clobbering
    // `samara_token` with the new IBA's token was logging the caller out.
    const preservedToken = localStorage.getItem("samara_token");
    const { data } = await api.post("/auth/register", payload);
    if (preservedToken) {
      // Downline registration — return credentials for optional per-new-IBA
      // side-effects (doc upload) but do NOT touch the caller's session.
      return {
        user:            data.user,
        token:           data.token,
        preservedToken,
        wasLoggedIn:     true,
      };
    }
    // Fresh public signup — auto-login the new IBA as before.
    localStorage.setItem("samara_token", data.token);
    setUser(data.user);
    return { user: data.user, token: data.token, wasLoggedIn: false };
  };

  const restoreSession = async (token) => {
    // Called after a downline-registration flow that briefly swapped the
    // localStorage token to perform new-IBA-scoped operations (e.g. KYC
    // uploads). Puts the caller's original token back and refreshes their
    // useAuth state so any downstream credits (direct-referral bump) show.
    localStorage.setItem("samara_token", token);
    await refresh();
  };

  const googleLogin = async (sessionId) => {
    const { data } = await api.post("/auth/google/callback", { session_id: sessionId });
    localStorage.setItem("samara_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("samara_token");
    localStorage.removeItem("samara_impersonator_token");
    setUser(null);
  };

  /** Swap the current session for a target user (admin impersonation).
   *  Stores the original admin token in `samara_impersonator_token` so the user
   *  can call `endImpersonation()` to return to their admin session. */
  const startImpersonation = ({ token, user: targetUser }) => {
    const current = localStorage.getItem("samara_token");
    if (current) localStorage.setItem("samara_impersonator_token", current);
    localStorage.setItem("samara_token", token);
    setUser(targetUser);
  };

  const endImpersonation = async () => {
    const back = localStorage.getItem("samara_impersonator_token");
    if (!back) { logout(); return; }
    localStorage.setItem("samara_token", back);
    localStorage.removeItem("samara_impersonator_token");
    // Hard reload so AuthProvider re-reads admin session cleanly.
    window.location.assign("/admin");
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, restoreSession, googleLogin, logout, refresh, startImpersonation, endImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
